import * as React from 'react';

import {
  Button,
  Col,
  ContentSection,
  Grid,
  Row,
  TextField,
} from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucidePlus } from 'lucide-react';

import { SalesDetailLayout } from '../../_layouts/SalesDetailLayout';

import { $openCreateSupplierDialog } from '~/modules/trade/_state/createSupplierDialog.state';
import {
  $focusedField,
  $remoteFocusPositions,
} from '~/modules/trade/_state/fieldPresence.state';
import {
  $flushTicketSupplierAutoSave,
  $ticketSupplierFormById,
  $updateTicketSupplierSelectField,
  $updateTicketSupplierSelectFields,
  $updateTicketSupplierTextField,
  DEFAULT_SUPPLIER_FORM,
  type TicketSupplierFormState,
} from '~/modules/trade/_state/scaleTicketSupplierForm.state';
import { useCreateSupplierPermissionQuery } from '~/modules/trade/hooks/queries/useCreateSupplierPermissionQuery';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { PurchaseLoadRecord } from '~/modules/trade/types/purchaseLoad';
import { FieldFocusIndicator } from '~/shared/components/FieldFocusIndicator';
import { SelectAccount } from '~/shared/components/select-fields/SelectAccount';
import { SelectPurchaseOrder } from '~/shared/components/select-fields/SelectPurchaseOrder';
import { SelectService } from '~/shared/components/select-fields/SelectService';
import { SelectSupplierLocationPair } from '~/shared/components/select-fields/SelectSupplierLocationPair';
import { type SelectableSupplierLocationPair } from '~/shared/components/select-fields/SelectSupplierLocationPairTouch';

type TextFieldKey = Extract<
  keyof TicketSupplierFormState,
  'freightTerms' | 'shippingCarrierName' | 'scac'
>;

type SelectFieldKey = Extract<
  keyof TicketSupplierFormState,
  'supplierAccountId' | 'purchaseOrderId' | 'serviceId' | 'carrierAccountId'
>;

type InboundTicketTabSupplierInfoProps = {
  ticket: PurchaseLoadRecord;
  disabled?: boolean;
};

//
// ⚛️ ⚛️ ⚛️ INBOUND TICKET TAB SUPPLIER INFO ⚛️ ⚛️ ⚛️
//
export const InboundTicketTabSupplierInfo: React.FC<
  InboundTicketTabSupplierInfoProps
> = ({ ticket, disabled = false }) => {
  const t = useTranslations();

  // Atoms
  const focusPositions = useAtomValue($remoteFocusPositions);
  const form =
    useAtomValue($ticketSupplierFormById).get(ticket.id) ??
    DEFAULT_SUPPLIER_FORM;
  const setFocusedField = useSetAtom($focusedField);
  const updateTextField = useSetAtom($updateTicketSupplierTextField);
  const updateSelectField = useSetAtom($updateTicketSupplierSelectField);
  const updateSelectFields = useSetAtom($updateTicketSupplierSelectFields);
  const flushAutoSave = useSetAtom($flushTicketSupplierAutoSave);
  const openCreateSupplierDialog = useSetAtom($openCreateSupplierDialog);

  // Data hooks
  const { data: permission } = useCreateSupplierPermissionQuery({});
  const canCreateSupplier = permission?.allowed ?? false;

  // Memoized values
  const supplierAccountIds = React.useMemo(
    () => (form.supplierAccountId ? [form.supplierAccountId] : undefined),
    [form.supplierAccountId],
  );

  const facilityIds = React.useMemo(
    () => (ticket.facilityId ? [ticket.facilityId] : undefined),
    [ticket.facilityId],
  );

  // Callbacks
  const handleTextFieldChange = React.useCallback(
    (field: TextFieldKey) => (value: string) => {
      updateTextField({ field, value, ticketId: ticket.id });
    },
    [updateTextField, ticket.id],
  );

  const handleSelectFieldChange = React.useCallback(
    (field: SelectFieldKey) => (value: string | undefined) => {
      updateSelectField({ field, value: value ?? null, ticketId: ticket.id });
    },
    [updateSelectField, ticket.id],
  );

  const handleSupplierCreated = React.useCallback(
    (supplierId: string) => {
      updateSelectFields({
        updates: {
          supplierAccountId: supplierId,
          supplierAddressId: null,
          purchaseOrderId: null,
          serviceId: null,
        },
        ticketId: ticket.id,
      });
    },
    [updateSelectFields, ticket.id],
  );

  const handleSupplierLocationChanged = React.useCallback(
    (value: string | undefined, entity?: SelectableSupplierLocationPair) => {
      updateSelectFields({
        updates: {
          supplierAccountId: entity?.accountId || null,
          supplierAddressId: value || null,
          purchaseOrderId: null,
          serviceId: null,
        },
        ticketId: ticket.id,
      });
    },
    [updateSelectFields, ticket.id],
  );

  const handleFieldFocus = React.useCallback(
    (field: string) => () => {
      setFocusedField(field);
    },
    [setFocusedField],
  );

  const handleFieldBlurFocus = React.useCallback(() => {
    setFocusedField(null);
  }, [setFocusedField]);

  const handleFieldBlur = React.useCallback(() => {
    flushAutoSave(ticket.id);
  }, [flushAutoSave, ticket.id]);

  const handleOpenCreateSupplierDialog = React.useCallback(() => {
    openCreateSupplierDialog({
      onSupplierSelected: handleSupplierCreated,
    });
  }, [openCreateSupplierDialog, handleSupplierCreated]);

  const handleRenderEmpty = React.useCallback(
    (query: string) => {
      if (query.length < 2) return undefined;
      return (
        <Row
          className="w-full cursor-pointer justify-center rounded-md p-2 text-sm font-medium text-primary hover:bg-primary/10"
          role="button"
          tabIndex={0}
          gap2
          onMouseDown={(e) => {
            e.preventDefault();
            openCreateSupplierDialog({
              onSupplierSelected: handleSupplierCreated,
              initialName: query,
            });
          }}
        >
          <LucidePlus className="size-4" />
          {t.createSupplierDialog.actions.createFromSearch}
        </Row>
      );
    },
    [openCreateSupplierDialog, handleSupplierCreated, t],
  );

  return (
    <SalesDetailLayout className="@InboundTicketTabSupplierInfo p-0">
      <ContentSection
        title={t.purchaseLoadDetail.sections.supplierInfo}
        titleClassName="font-bold text-neutral"
      >
        <Grid cols1 gap4 className="md:grid-cols-2">
          <Row yEnd gap2>
            <FieldFocusIndicator
              fieldId="supplierAccountId"
              focusPositions={focusPositions}
              className="flex-1"
            >
              <div
                onFocus={handleFieldFocus('supplierAccountId')}
                onBlur={handleFieldBlurFocus}
              >
                <SelectSupplierLocationPair
                  className="flex-1"
                  label={t.purchaseLoadDetail.fields.supplier}
                  value={form.supplierAddressId}
                  onChange={handleSupplierLocationChanged}
                  isDisabled={disabled}
                  placeholder={t.purchaseLoadDetail.placeholders.selectSupplier}
                  renderEmpty={
                    canCreateSupplier ? handleRenderEmpty : undefined
                  }
                />
              </div>
            </FieldFocusIndicator>
            {canCreateSupplier && (
              <Button
                className="h-input w-10"
                size="icon"
                variant="outline"
                icon={<LucidePlus />}
                isDisabled={disabled}
                onPress={handleOpenCreateSupplierDialog}
                aria-label={t.createSupplierDialog.actions.addSupplier}
              />
            )}
          </Row>

          <FieldFocusIndicator
            fieldId="purchaseOrderId"
            focusPositions={focusPositions}
          >
            <div
              onFocus={handleFieldFocus('purchaseOrderId')}
              onBlur={handleFieldBlurFocus}
            >
              <SelectPurchaseOrder
                label={t.purchaseLoadDetail.fields.purchaseOrder}
                value={form.purchaseOrderId}
                onChange={handleSelectFieldChange('purchaseOrderId')}
                isDisabled={disabled}
                placeholder={
                  t.purchaseLoadDetail.placeholders.linkPurchaseOrder
                }
                accountIds={supplierAccountIds}
                facilityIds={facilityIds}
              />
            </div>
          </FieldFocusIndicator>

          <FieldFocusIndicator
            fieldId="serviceId"
            focusPositions={focusPositions}
          >
            <div
              onFocus={handleFieldFocus('serviceId')}
              onBlur={handleFieldBlurFocus}
            >
              <SelectService
                label={t.purchaseLoadDetail.fields.service}
                value={form.serviceId}
                onChange={handleSelectFieldChange('serviceId')}
                isDisabled={disabled}
                placeholder={t.purchaseLoadDetail.placeholders.linkService}
                accountIds={supplierAccountIds}
              />
            </div>
          </FieldFocusIndicator>
        </Grid>
      </ContentSection>

      <ContentSection
        title={t.purchaseLoadDetail.sections.carrierInfo}
        titleClassName="font-bold text-neutral"
      >
        <Grid cols1 gap4 className="md:grid-cols-2">
          <FieldFocusIndicator
            fieldId="carrierAccountId"
            focusPositions={focusPositions}
          >
            <div
              onFocus={handleFieldFocus('carrierAccountId')}
              onBlur={handleFieldBlurFocus}
            >
              <SelectAccount
                label={t.purchaseLoadDetail.fields.carrierAccount}
                value={form.carrierAccountId}
                onChange={handleSelectFieldChange('carrierAccountId')}
                isDisabled={disabled}
                placeholder={t.purchaseLoadDetail.placeholders.selectCarrier}
              />
            </div>
          </FieldFocusIndicator>

          <FieldFocusIndicator
            fieldId="freightTerms"
            focusPositions={focusPositions}
          >
            <div
              onFocus={handleFieldFocus('freightTerms')}
              onBlur={handleFieldBlurFocus}
            >
              <TextField
                label={t.purchaseLoadDetail.fields.freightTerms}
                value={form.freightTerms ?? ''}
                onChange={handleTextFieldChange('freightTerms')}
                onBlur={handleFieldBlur}
                isDisabled={disabled}
                placeholder={
                  t.purchaseLoadDetail.placeholders.enterFreightTerms
                }
              />
            </div>
          </FieldFocusIndicator>

          <FieldFocusIndicator
            fieldId="shippingCarrierName"
            focusPositions={focusPositions}
          >
            <div
              onFocus={handleFieldFocus('shippingCarrierName')}
              onBlur={handleFieldBlurFocus}
            >
              <TextField
                label={t.purchaseLoadDetail.fields.shippingCarrierName}
                value={form.shippingCarrierName ?? ''}
                onChange={handleTextFieldChange('shippingCarrierName')}
                onBlur={handleFieldBlur}
                isDisabled={disabled}
                placeholder={t.purchaseLoadDetail.placeholders.enterCarrierName}
              />
            </div>
          </FieldFocusIndicator>

          <FieldFocusIndicator fieldId="scac" focusPositions={focusPositions}>
            <div
              onFocus={handleFieldFocus('scac')}
              onBlur={handleFieldBlurFocus}
            >
              <TextField
                label={t.purchaseLoadDetail.fields.scac}
                value={form.scac ?? ''}
                onChange={handleTextFieldChange('scac')}
                onBlur={handleFieldBlur}
                isDisabled={disabled}
                placeholder={t.purchaseLoadDetail.placeholders.enterScac}
              />
            </div>
          </FieldFocusIndicator>
        </Grid>
      </ContentSection>

      <ContentSection
        title={t.purchaseLoadDetail.sections.facilityInfo}
        titleClassName="font-bold text-neutral"
      >
        <div className="rounded-md border border-neutral/20 bg-neutral/5 px-3 py-2">
          <span className="text-sm font-medium">
            {ticket.facility?.facilityName ??
              t.purchaseLoadDetail.fields.unknownFacility}
          </span>
        </div>
      </ContentSection>

      {ticket.sourceLoadId && (
        <ContentSection
          title={t.purchaseLoadDetail.sections.sourceLoadInfo}
          titleClassName="font-bold text-neutral"
        >
          <Grid cols1 gap4 className="md:grid-cols-2">
            <Col>
              <span className="text-xs text-foreground/60">
                {t.purchaseLoadDetail.fields.sourceFacility}
              </span>
              <div className="rounded-md border border-neutral/20 bg-neutral/5 px-3 py-2">
                <span className="text-sm font-medium">
                  {ticket.sourceFacility?.facilityName ??
                    t.purchaseLoadDetail.fields.unknownFacility}
                </span>
              </div>
            </Col>
          </Grid>
        </ContentSection>
      )}
    </SalesDetailLayout>
  );
};
